import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FaStore } from 'react-icons/fa';
import { MdDeliveryDining } from 'react-icons/md';
import HourSelector from '../../../../../../../../../components/common/HourSelector/HourSelector';
import { GetAllThirdPartyProviders } from '../../../../../../../../../crud/configuration_api/third_party_providers';
import './CreatePedidoForm.scss';

type PedidoType = 'delivery' | 'pickup';

interface CreatePedidoFormProps {
  onSubmit: (data: CreatePedidoFormData) => void;
  onCancel: () => void;
}

export interface CreatePedidoFormData {
  pedido_type: PedidoType;
  client_name: string;
  hora_entrega?: string;
  third_party_provider_id?: number;
  third_party_provider_code?: string;
}

// Visual config for known providers (keyed by lowercase name)
const PROVIDER_VISUALS: Record<string, { logo: string; className: string }> = {
  'uber eats': { logo: 'uber-eats.png', className: 'uber-eats' },
  'rappi':     { logo: 'rappi.png', className: 'rappi' },
  'pedidos ya': { logo: 'pedidos-ya.png', className: 'pedidos-ya' },
  'justo':     { logo: 'JUSTO.png', className: 'justo' },
  'didi food': { logo: 'didi-food.jpg', className: 'didi-food' },
};

function getProviderVisuals(name: string): { logo: string; className: string } {
  return PROVIDER_VISUALS[name.toLowerCase()] ?? {
    logo: '',
    className: name.toLowerCase().replace(/\s+/g, '-'),
  };
}

const CreatePedidoForm: React.FC<CreatePedidoFormProps> = ({ onSubmit, onCancel }) => {
  const [pedidoType, setPedidoType] = useState<PedidoType>('delivery');
  const [clientName, setClientName] = useState('');
  const [horaEntrega, setHoraEntrega] = useState('');
  const [selectedQuickTime, setSelectedQuickTime] = useState<string>('');
  const [orderCode, setOrderCode] = useState('');
  const [selectedThirdPartyProviderId, setSelectedThirdPartyProviderId] = useState<number | ''>('');
  const [availableProviders, setAvailableProviders] = useState<{ id: number; name: string; logo: string; className: string }[]>([]);

  // Fetch providers from the API so IDs match the database
  useEffect(() => {
    GetAllThirdPartyProviders()
      .then((response) => {
        const providers = (response.data || []).map((p: { third_party_provider_id: number; name: string }) => {
          const visuals = getProviderVisuals(p.name);
          return {
            id: p.third_party_provider_id,
            name: p.name,
            logo: visuals.logo,
            className: visuals.className,
          };
        });
        setAvailableProviders(providers);
      })
      .catch((err) => console.error('Error fetching third party providers:', err));
  }, []);

  const handleQuickTimeSelect = (minutes: number, label: string) => {
    const now = new Date();
    const totalMinutes = now.getHours() * 60 + now.getMinutes() + minutes;
    
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    
    const roundedMinutes = Math.round(newMinutes / 5) * 5;
    
    const finalHours = roundedMinutes >= 60 ? (newHours + 1) % 24 : newHours;
    const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes;
    
    const timeString = `${finalHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
    
    setHoraEntrega(timeString);
    setSelectedQuickTime(label);
  };

  const handleNowSelect = () => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Round to nearest 5 minutes
    const roundedMinutes = Math.round(currentMinutes / 5) * 5;
    const finalHours = roundedMinutes >= 60 ? (currentHours + 1) % 24 : currentHours;
    const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes;
    
    const timeString = `${finalHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
    
    setHoraEntrega(timeString);
    setSelectedQuickTime('ahora');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientName.trim() || !horaEntrega) {
      return;
    }

    // For delivery type, require provider and code
    if (pedidoType === 'delivery' && (!selectedThirdPartyProviderId || !orderCode.trim())) {
      return;
    }

    onSubmit({
      pedido_type: pedidoType,
      client_name: clientName,
      hora_entrega: horaEntrega,
      third_party_provider_id: pedidoType === 'delivery' ? selectedThirdPartyProviderId as number : undefined,
      third_party_provider_code: pedidoType === 'delivery' ? orderCode : undefined,
    });
  };

  return (
    <div className="sales-create-pedido-form">
      <div className="sales-pedido-header">
        <h3>Crear Nuevo Pedido</h3>
      </div>

      <Form onSubmit={handleSubmit}>
        {/* Pedido Type Selector */}
        <div className="sales-pedido-type-selector">
          <button
            type="button"
            className={`sales-type-option ${pedidoType === 'delivery' ? 'active' : ''}`}
            onClick={() => setPedidoType('delivery')}
          >
            <MdDeliveryDining size={28} />
            <span>Delivery</span>
          </button>
          <button
            type="button"
            className={`sales-type-option ${pedidoType === 'pickup' ? 'active' : ''}`}
            onClick={() => setPedidoType('pickup')}
          >
            <FaStore size={24} />
            <span>Retiro en Local</span>
          </button>
        </div>

        <Form.Group controlId="clientName" className="sales-formgroup horizontal">
          <Form.Label>Nombre del Cliente</Form.Label>
          <Form.Control
            type="text"
            placeholder="Ingrese el nombre del cliente"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group controlId="horaEntrega" className="sales-formgroup horizontal">
          <Form.Label>Hora de {pedidoType === 'delivery' ? 'Entrega' : 'Retiro'}</Form.Label>
          <HourSelector
            id="horaEntrega"
            value={horaEntrega}
            onChange={(time) => {
              setHoraEntrega(time);
              setSelectedQuickTime('');
            }}
            disabled={false}
            allowPrev={false}
          />
        </Form.Group>

        {!horaEntrega && (
          <div className="sales-quick-time-section">
            <label className="sales-quick-time-label">Selección Rápida</label>
            <div className="sales-quick-time-buttons">
              <button
                type="button"
                className={`sales-quick-time-btn ahora ${selectedQuickTime === 'ahora' ? 'active' : ''}`}
                onClick={handleNowSelect}
              >
                AHORA
              </button>
              <button
                type="button"
                className={`sales-quick-time-btn ${selectedQuickTime === '15min' ? 'active' : ''}`}
                onClick={() => handleQuickTimeSelect(15, '15min')}
              >
                15 min
              </button>
              <button
                type="button"
                className={`sales-quick-time-btn ${selectedQuickTime === '30min' ? 'active' : ''}`}
                onClick={() => handleQuickTimeSelect(30, '30min')}
              >
                30 min
              </button>
              <button
                type="button"
                className={`sales-quick-time-btn ${selectedQuickTime === '45min' ? 'active' : ''}`}
                onClick={() => handleQuickTimeSelect(45, '45min')}
              >
                45 min
              </button>
              <button
                type="button"
                className={`sales-quick-time-btn ${selectedQuickTime === '1h' ? 'active' : ''}`}
                onClick={() => handleQuickTimeSelect(60, '1h')}
              >
                1 hora
              </button>
            </div>
          </div>
        )}

        {/* Delivery App Information - Only show for delivery type */}
        {pedidoType === 'delivery' && (
          <>
            <Form.Group controlId="orderCode" className="sales-formgroup horizontal">
              <Form.Label>Número de Orden</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: #12345"
                value={orderCode}
                onChange={(e) => setOrderCode(e.target.value)}
                required
              />
            </Form.Group>

            <div className="sales-provider-section">
              <Form.Label>Aplicación de Delivery</Form.Label>
              <div className="sales-provider-options">
                {availableProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className={`sales-provider-option ${provider.className} ${
                      selectedThirdPartyProviderId === provider.id ? 'active' : ''
                    }`}
                    onClick={() => setSelectedThirdPartyProviderId(provider.id)}
                  >
                    <div className="sales-provider-logo">
                      {provider.logo ? (
                        <img 
                          src={`${process.env.PUBLIC_URL}/icons/providers/${provider.logo}`}
                          alt={provider.name}
                        />
                      ) : (
                        <MdDeliveryDining size={24} />
                      )}
                    </div>
                    <span className="sales-provider-name">{provider.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="sales-form-actions">
          <Button
            variant="none"
            className="sales-form-button-cancel"
            onClick={onCancel}
            type="button"
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            className="sales-form-button-submit"
            type="submit"
            disabled={
              !clientName.trim() || 
              !horaEntrega || 
              (pedidoType === 'delivery' && (!orderCode.trim() || !selectedThirdPartyProviderId))
            }
          >
            Continuar al Pedido
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreatePedidoForm;
